/**
 * Servicios para gestión de Miembros (Member)
 *
 * Member representa la membresía de una Person en un Club.
 * Los datos personales están en Person, aquí solo datos de membresía.
 */

import {
  where,
  orderBy,
  Timestamp,
  doc,
  updateDoc,
  increment,
  onSnapshot,
  query,
  collection,
  limit,
  getDocs,
  writeBatch,
  deleteDoc,
  runTransaction,
  getDoc,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import {
  getById,
  getAll,
  create,
  update,
  getPaginated,
  PaginatedResult,
  convertTimestamps,
  subscribeToCollection,
} from "./base";
import type {
  Club,
  Member,
  MemberStatus,
  MemberStatusChange,
  MemberWithPerson,
  MemberLegacy,
  Person,
} from "@/types";
import { QueryDocumentSnapshot, DocumentData } from "firebase/firestore";
import { personService } from "./persons";

// ==================== HELPERS ====================

/**
 * Combina Member con datos de Person para compatibilidad con código legacy
 * @deprecated Preferir usar Member + Person separados
 */
export function toMemberLegacy(member: Member, person: Person): MemberLegacy {
  return {
    ...member,
    // Objeto Person completo (nueva arquitectura)
    person,
    // Campos flat (legacy - mantenidos por compatibilidad)
    firstName: person.firstName,
    lastName: person.lastName,
    fullName: person.fullName,
    memberType: person.memberType,
    idDocumentType: person.idDocumentType,
    idDocumentNumber: person.idDocumentNumber,
    bloodType: person.bloodType,
    birthDate: person.birthDate,
    gender: person.gender,
    phone: person.phone,
    email: person.email,
    healthInsurance: person.healthInsurance,
    photoUrl: person.photoUrl,
    photoPath: person.photoPath,
    documentFrontUrl: person.documentFrontUrl,
    documentFrontPath: person.documentFrontPath,
    epsDocumentUrl: person.epsDocumentUrl,
    epsDocumentPath: person.epsDocumentPath,
    address: person.address,
    emergencyContact: person.emergencyContact,
    linkedUserId: person.linkedUserId,
    linkedAt: person.linkedAt,
  };
}

/**
 * Combina Member con Person completo
 */
export function toMemberWithPerson(
  member: Member,
  person: Person,
): MemberWithPerson {
  return {
    ...member,
    person,
  };
}

/**
 * Normaliza un string para comparación tolerante (sin tildes, minúsculas,
 * solo alfanumérico). Usado para detectar coincidencias de nombres.
 */
function normalizeName(s: string): string {
  return String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[^a-z0-9 ]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Calcula similitud Jaccard entre dos nombres (0..1).
 * Retorna 1.0 si son iguales tras normalizar, 0.9 si uno contiene al otro,
 * y la fracción de tokens comunes en otro caso.
 * Threshold recomendado para "misma persona": ≥ 0.5.
 */
export function nameSimilarity(a: string, b: string): number {
  const na = normalizeName(a);
  const nb = normalizeName(b);
  if (!na || !nb) return 0;
  if (na === nb) return 1;
  if (na.includes(nb) || nb.includes(na)) return 0.9;
  const ta = new Set(na.split(" ").filter(Boolean));
  const tb = new Set(nb.split(" ").filter(Boolean));
  let inter = 0;
  ta.forEach((t) => {
    if (tb.has(t)) inter++;
  });
  return inter / Math.max(ta.size, tb.size);
}

// ==================== MEMBER ====================

export const memberService = {
  async getById(id: string): Promise<Member | null> {
    return getById<Member>("members", id);
  },

  /**
   * Obtener miembro con datos de persona
   */
  async getByIdWithPerson(id: string): Promise<MemberWithPerson | null> {
    const member = await getById<Member>("members", id);
    if (!member) return null;

    const person = await personService.getById(member.personId);
    if (!person) return null;

    return toMemberWithPerson(member, person);
  },

  /**
   * Obtener miembro en formato legacy (con datos de persona inlined)
   * @deprecated Usar getByIdWithPerson
   */
  async getByIdLegacy(id: string): Promise<MemberLegacy | null> {
    const member = await getById<Member>("members", id);
    if (!member) return null;

    const person = await personService.getById(member.personId);
    if (!person) return null;

    return toMemberLegacy(member, person);
  },

  async getByClub(clubId: string): Promise<Member[]> {
    // Query simple sin orderBy para evitar requerir índice compuesto
    // El orden se maneja en el cliente
    const members = await getAll<Member>("members", [
      where("clubId", "==", clubId),
    ]);
    // Ordenar por createdAt descendente en el cliente
    return members.sort((a, b) => {
      const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(0);
      const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(0);
      return dateB.getTime() - dateA.getTime();
    });
  },

  /**
   * Obtener miembros de un club con datos de persona
   */
  async getByClubWithPerson(clubId: string): Promise<MemberWithPerson[]> {
    const members = await this.getByClub(clubId);
    if (members.length === 0) return [];

    const personIds = [...new Set(members.map((m) => m.personId))];
    const persons = await personService.getByIds(personIds);
    const personMap = new Map(persons.map((p) => [p.id, p]));

    return members
      .filter((m) => personMap.has(m.personId))
      .map((m) => toMemberWithPerson(m, personMap.get(m.personId)!));
  },

  /**
   * Obtener miembros de un club con datos de persona (formato legacy)
   * @deprecated Usar getByClubWithPerson
   */
  async getByClubLegacy(clubId: string): Promise<MemberLegacy[]> {
    const members = await this.getByClub(clubId);
    if (members.length === 0) return [];

    const personIds = [...new Set(members.map((m) => m.personId))];
    const persons = await personService.getByIds(personIds);
    const personMap = new Map(persons.map((p) => [p.id, p]));

    return members
      .filter((m) => personMap.has(m.personId))
      .map((m) => toMemberLegacy(m, personMap.get(m.personId)!));
  },

  async getByClubPaginated(
    clubId: string,
    pageSize: number,
    lastDoc?: QueryDocumentSnapshot<DocumentData>,
  ): Promise<PaginatedResult<Member>> {
    return getPaginated<Member>("members", pageSize, lastDoc, [
      where("clubId", "==", clubId),
      orderBy("createdAt", "desc"),
    ]);
  },

  async getByPerson(personId: string): Promise<Member[]> {
    return getAll<Member>("members", [
      where("personId", "==", personId),
      orderBy("createdAt", "desc"),
    ]);
  },

  async getByUnit(unitId: string): Promise<Member[]> {
    return getAll<Member>("members", [
      where("unitId", "==", unitId),
      where("status", "==", "active"),
    ]);
  },

  async findExisting(
    personId: string,
    clubId: string,
    periodId: string,
  ): Promise<Member | null> {
    const members = await getAll<Member>("members", [
      where("personId", "==", personId),
      where("clubId", "==", clubId),
      where("periodId", "==", periodId),
    ]);
    return members.length > 0 ? members[0] : null;
  },

  async create(
    data: Omit<Member, "id" | "createdAt" | "updatedAt">,
    userId: string,
  ): Promise<string> {
    // M4-fix: forzar personName/personPhoto desde Person (fuente de verdad).
    // Esto evita que un caller pase datos stale del formulario y desincronice
    // a member del person.
    const enriched: typeof data = { ...data };
    if (data.personId) {
      try {
        const personSnap = await getDoc(doc(db, "persons", data.personId));
        if (personSnap.exists()) {
          const p = personSnap.data() as {
            fullName?: string;
            photoUrl?: string;
          };
          if (p.fullName) (enriched as any).personName = p.fullName;
          (enriched as any).personPhoto = p.photoUrl ?? "";
        }
      } catch {
        // Si falla la lectura, conservar lo que vino en data
      }
    }

    // Anti double-submit: usar docId determinístico cuando hay personId+clubId
    // y verificar atómicamente con transacción que no exista una membresía
    // activa previa para esa persona en ese club. Si dos requests entran a la
    // vez (doble-click), solo el primero crea el doc; el segundo recibe error.
    if (enriched.personId && enriched.clubId) {
      const deterministicId = `${enriched.clubId}__${enriched.personId}`;
      const ref = doc(db, "members", deterministicId);
      try {
        await runTransaction(db, async (tx) => {
          const snap = await tx.get(ref);
          if (snap.exists()) {
            const existing = snap.data() as Member;
            if (existing.status !== "inactive") {
              throw new Error(
                "Esta persona ya tiene una membresía en este club.",
              );
            }
            // Existía pero estaba inactiva → re-activar sobrescribiendo
          }
          tx.set(ref, {
            ...enriched,
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
            createdBy: userId,
          });
        });
        // Incrementar contadores fuera de la transacción (otra entidad)
        const clubRef = doc(db, "clubs", enriched.clubId);
        await updateDoc(clubRef, {
          memberCount: increment(1),
          ...(enriched.status === "active"
            ? { activeCount: increment(1) }
            : {}),
        });
        return deterministicId;
      } catch (err) {
        // Re-lanzar para que el caller muestre el mensaje
        throw err;
      }
    }
    // Fallback: comportamiento original con autoId (sin personId/clubId)
    const id = await create<Member>("members", enriched, userId);
    if (enriched.clubId) {
      const clubRef = doc(db, "clubs", enriched.clubId);
      await updateDoc(clubRef, {
        memberCount: increment(1),
        ...(enriched.status === "active" ? { activeCount: increment(1) } : {}),
      });
    }
    return id;
  },

  /**
   * F11 — Traslada una membresía a otro club (solo admin desde la UI).
   *
   * Como el docId es determinístico (`{clubId}__{personId}`), trasladar NO es
   * un update de campo: es crear el doc del club destino y borrar el del club
   * origen en una transacción, y luego cuadrar los contadores de ambos clubes.
   * Deja rastro: `originClubId`/`transferredAt`/`transferredBy` en el member
   * nuevo y una entrada "transferred" en memberStatusHistory del club origen.
   *
   * NO toca inscripciones a eventos (histórico del evento — para eso existe
   * adminRegistrationService.transferToClub) ni clases/asistencia del club
   * origen (quedan como historial de ese club).
   */
  async transferToClub(
    memberId: string,
    targetClubId: string,
    userId: string,
    reason?: string,
    /**
     * F14: si el traslado viene de una solicitud aprobada, su cierre va DENTRO
     * de la misma transacción — y para el director de origen es obligatorio:
     * las rules sólo le permiten crear el member en el club destino si el
     * mismo batch marca la solicitud como `completed`.
     */
    opts?: { transferRequestId?: string; decisionNote?: string },
  ): Promise<string> {
    const preview = await getById<Member>("members", memberId);
    if (!preview) throw new Error("Miembro no encontrado");
    if (!preview.personId) throw new Error("La ficha no tiene persona vinculada");
    if (preview.clubId === targetClubId)
      throw new Error("El miembro ya pertenece a ese club");
    // Cargos y traslado (afinado 2026-08-10 con el caso de una instructora):
    //
    // · Un cargo INTERNO del club (instructor, consejero, secretario…) vive
    //   solo en member.role y no otorga permisos del sistema. NO bloquea el
    //   traslado: la persona llega al club nuevo como "member" y allá le
    //   asignan el cargo que corresponda — los cargos no viajan.
    //
    // · Una DIRECCIÓN sí vive en roleAssignments (+ espejo users.directorClubIds
    //   que leen las rules): trasladar al director dejaría permisos huérfanos
    //   apuntando al club viejo. Eso se resuelve ANTES con el flujo de
    //   transferir la dirección.
    const conCargo = Boolean(preview.role && preview.role !== "member");
    if (conCargo) {
      const persona = await getById<Person>("persons", preview.personId);
      if (persona?.linkedUserId) {
        const direcciones = await getDocs(
          query(
            collection(db, "roleAssignments"),
            where("userId", "==", persona.linkedUserId),
            where("scopeId", "==", preview.clubId),
            where("status", "==", "active"),
          ),
        );
        if (!direcciones.empty)
          throw new Error(
            "Esta persona dirige el club o una sección. Transfiere o revoca " +
              "su dirección antes de trasladarla.",
          );
      }
    }

    const targetClub = await getById<Club>("clubs", targetClubId);
    if (!targetClub) throw new Error("El club destino no existe");
    if (targetClub.status !== "active")
      throw new Error("El club destino no está activo");

    const sourceClubId = preview.clubId;
    const newId = `${targetClubId}__${preview.personId}`;
    const oldRef = doc(db, "members", memberId);
    const newRef = doc(db, "members", newId);
    const requestRef = opts?.transferRequestId
      ? doc(db, "memberTransferRequests", opts.transferRequestId)
      : null;

    let movedStatus: MemberStatus = preview.status;
    await runTransaction(db, async (tx) => {
      // Leer AMBOS docs dentro de la TX: sin leer oldRef, dos traslados
      // concurrentes del mismo miembro no colisionan (delete ciego = no-op
      // sobre doc inexistente) y la persona quedaría duplicada con el club
      // origen decrementado dos veces.
      const [oldSnap, existing, reqSnap] = await Promise.all([
        tx.get(oldRef),
        tx.get(newRef),
        requestRef ? tx.get(requestRef) : Promise.resolve(null),
      ]);
      if (!oldSnap.exists())
        throw new Error("El miembro ya fue trasladado o eliminado.");
      const member = oldSnap.data() as Member;
      if (existing.exists() && existing.data().status !== "inactive") {
        throw new Error("Esta persona ya tiene una membresía activa en el club destino.");
      }
      // La solicitud debe seguir viva y hablar de ESTE traslado.
      if (requestRef) {
        const req = reqSnap?.exists() ? reqSnap.data() : null;
        if (!req || req.status !== "pending")
          throw new Error("La solicitud de traslado ya fue resuelta o cancelada.");
        if (req.originMemberId !== memberId || req.targetClubId !== targetClubId)
          throw new Error("La solicitud no corresponde a este traslado.");
      }
      movedStatus = member.status;

      const { id: _omit, ...data } = { ...member, id: memberId };
      // Lo que es DEL CLUB no viaja: el cargo interno (role/staffSection) lo
      // asigna el club nuevo, y la unidad pertenece a la estructura del viejo.
      delete (data as Record<string, unknown>).staffSection;
      delete (data as Record<string, unknown>).unitId;
      tx.set(newRef, {
        ...data,
        role: "member",
        clubId: targetClubId,
        associationId: targetClub.associationId ?? member.associationId ?? null,
        originClubId: sourceClubId,
        transferredAt: Timestamp.now(),
        transferredBy: userId,
        updatedAt: Timestamp.now(),
        updatedBy: userId,
      });
      tx.delete(oldRef);
      if (requestRef) {
        tx.update(requestRef, {
          status: "completed",
          decidedBy: userId,
          decidedAt: Timestamp.now(),
          ...(opts?.decisionNote?.trim()
            ? { decisionNote: opts.decisionNote.trim() }
            : {}),
          updatedAt: Timestamp.now(),
        });
      }
      // Los contadores de ambos clubes ya NO se escriben aquí: el director de
      // ORIGEN no puede tocar el doc del club DESTINO (rules), y un tx parcial
      // dejaba descuadre. El trigger member-counters (functions) los recuenta
      // desde la verdad con cada escritura de members.
    });

    // Historial en el club origen (best-effort, fuera de la TX).
    await create<MemberStatusChange>("memberStatusHistory", {
      memberId,
      clubId: sourceClubId,
      previousStatus: movedStatus,
      newStatus: "transferred",
      reason:
        (reason ||
          `Trasladado al club ${targetClub.name}${targetClub.isFusion ? " (club fusión)" : ""}`) +
        (conCargo
          ? ` · su cargo (${preview.role}) no viaja: llega como miembro`
          : ""),
      effectiveDate: Timestamp.now(),
      changedBy: userId,
    } as Omit<MemberStatusChange, "id">, userId);

    return newId;
  },

  /**
   * F11 — Agrega una persona a un club FUSIÓN de forma ADITIVA: crea una
   * SEGUNDA membresía en la fusión sin tocar la del club real, guardando
   * `originClubId` para poder disolver después. Reutiliza `create()` (que ya
   * valida duplicados y cuadra contadores del club fusión).
   */
  async addToFusion(
    sourceMember: Member,
    fusionClubId: string,
    userId: string,
  ): Promise<string> {
    if (!sourceMember.personId)
      throw new Error("La ficha no tiene persona vinculada");
    const {
      id: _omit,
      createdAt: _c,
      updatedAt: _u,
      // En la fusión entra como miembro raso: los cargos son del club real.
      staffSection: _s,
      roleHistory: _rh,
      // FusionPanel entrega MemberWithPerson: sin esto, el snapshot COMPLETO
      // de la Person (PII) quedaría escrito dentro del doc member.
      person: _p,
      ...data
    } = sourceMember as Member & {
      createdAt?: Timestamp;
      updatedAt?: Timestamp;
      person?: Person;
    };
    const payload = {
      ...data,
      clubId: fusionClubId,
      originClubId: sourceMember.clubId,
      role: "member",
      registrationDate: Timestamp.now(),
    } as Record<string, unknown>;
    // El SDK web rechaza `undefined` como valor de campo: limpiar antes de escribir.
    for (const k of Object.keys(payload)) {
      if (payload[k] === undefined) delete payload[k];
    }
    return memberService.create(payload as unknown as Omit<Member, "id">, userId);
  },

  async update(id: string, data: Partial<Member>): Promise<void> {
    // M4-fix: forzar personName/personPhoto desde Person si el caller no
    // intenta cambiar personId. Si cambia personId, releer también.
    const enriched: Partial<Member> = { ...data };
    let pid = data.personId;
    if (!pid) {
      const current = await getById<Member>("members", id);
      pid = current?.personId;
    }
    if (pid) {
      try {
        const personSnap = await getDoc(doc(db, "persons", pid));
        if (personSnap.exists()) {
          const p = personSnap.data() as {
            fullName?: string;
            photoUrl?: string;
          };
          if (p.fullName) (enriched as any).personName = p.fullName;
          (enriched as any).personPhoto = p.photoUrl ?? "";
        }
      } catch {
        // ignore
      }
    }
    return update<Member>("members", id, enriched);
  },

  async updateStatus(id: string, status: Member["status"]): Promise<void> {
    // Ajustar activeCount del club según transición previa→nueva.
    // (Equivalente a updateStatusWithHistory pero sin escribir historial.)
    const member = await getById<Member>("members", id);
    if (!member) throw new Error("Miembro no encontrado");
    const previousStatus = member.status;
    await update<Member>("members", id, { status });
    if (member.clubId && previousStatus !== status) {
      const wasActive = previousStatus === "active";
      const isActive = status === "active";
      if (wasActive && !isActive) {
        await updateDoc(doc(db, "clubs", member.clubId), {
          activeCount: increment(-1),
        });
      } else if (!wasActive && isActive) {
        await updateDoc(doc(db, "clubs", member.clubId), {
          activeCount: increment(1),
        });
      }
    }
  },

  async updateStatusWithHistory(
    id: string,
    newStatus: MemberStatus,
    reason: string | undefined,
    userId: string,
  ): Promise<void> {
    // Obtener miembro actual
    const member = await getById<Member>("members", id);
    if (!member) throw new Error("Miembro no encontrado");

    const previousStatus = member.status;

    // Actualizar estado del miembro
    await update<Member>("members", id, {
      status: newStatus,
      ...(reason && { statusReason: reason }),
    });

    // C2/C3: Ajustar activeCount según transición de estado
    if (member.clubId && previousStatus !== newStatus) {
      const wasActive = previousStatus === "active";
      const isActive = newStatus === "active";
      if (wasActive && !isActive) {
        await updateDoc(doc(db, "clubs", member.clubId), {
          activeCount: increment(-1),
        });
      } else if (!wasActive && isActive) {
        await updateDoc(doc(db, "clubs", member.clubId), {
          activeCount: increment(1),
        });
      }
    }

    // Registrar cambio en historial
    await create<MemberStatusChange>(
      "memberStatusHistory",
      {
        memberId: id,
        personId: member.personId,
        clubId: member.clubId,
        previousStatus,
        newStatus,
        ...(reason && { reason }),
        effectiveDate: Timestamp.now(),
        changedBy: userId,
      },
      userId,
    );
  },

  async getStatusHistory(memberId: string): Promise<MemberStatusChange[]> {
    return getAll<MemberStatusChange>("memberStatusHistory", [
      where("memberId", "==", memberId),
      orderBy("effectiveDate", "desc"),
    ]);
  },

  async assignToUnit(id: string, unitId: string): Promise<void> {
    return update<Member>("members", id, { unitId });
  },

  async delete(id: string): Promise<void> {
    // F3: Soft delete — marcar como inactivo + decrementar contadores
    const member = await getById<Member>("members", id);
    if (!member) return;
    await update<Member>("members", id, { status: "inactive" });
    if (member.clubId && member.status === "active") {
      await updateDoc(doc(db, "clubs", member.clubId), {
        activeCount: increment(-1),
      });
    }
  },

  /**
   * BUG-002: Hard delete — elimina físicamente al miembro y sus datos relacionados.
   *
   * Diferencias con delete():
   * - Borra el doc del miembro (no solo cambia status).
   * - Borra ClassProgress, Achievements y EventRegistrations vinculados a esta
   *   pareja (personId + clubId). Datos de OTROS clubes de la persona se preservan.
   * - Decrementa memberCount y (si estaba activo) activeCount del club.
   *
   * IMPORTANTE: irreversible. Solo debe llamarse desde flujos con confirmación
   * fuerte (escribir nombre, doble confirmación, gate de permisos).
   */
  async hardDelete(id: string): Promise<void> {
    const member = await getById<Member>("members", id);
    if (!member) return;

    const { clubId, personId, status } = member;
    if (!clubId || !personId) {
      // Datos inconsistentes: borrar solo el doc principal sin tocar contadores.
      await deleteDoc(doc(db, "members", id));
      return;
    }

    // 0) Bloqueo de integridad: la persona no puede ser director del club.
    //    Si lo fuera, dejaríamos el club sin director referenciado.
    const clubSnap = await getDoc(doc(db, "clubs", clubId));
    if (clubSnap.exists() && clubSnap.data()?.directorId === personId) {
      throw new Error(
        "No se puede eliminar permanentemente: esta persona es el director del club. Asigna otro director antes de continuar.",
      );
    }

    // 1) Recolectar dependencias de la pareja (personId + clubId)
    const [progressSnap, achievementsSnap, registrationsSnap] =
      await Promise.all([
        getDocs(
          query(
            collection(db, "classProgress"),
            where("personId", "==", personId),
            where("clubId", "==", clubId),
          ),
        ),
        getDocs(
          query(
            collection(db, "achievements"),
            where("personId", "==", personId),
            where("clubId", "==", clubId),
          ),
        ),
        getDocs(
          query(
            collection(db, "eventRegistrations"),
            where("personId", "==", personId),
            where("clubId", "==", clubId),
          ),
        ),
      ]);

    // 2) Construir batch (Firestore: máx 500 ops). Si excede, dividir.
    const docsToDelete = [
      ...progressSnap.docs,
      ...achievementsSnap.docs,
      ...registrationsSnap.docs,
    ];

    const CHUNK = 450; // margen para incluir el update del club + member
    for (let i = 0; i < docsToDelete.length; i += CHUNK) {
      const batch = writeBatch(db);
      const slice = docsToDelete.slice(i, i + CHUNK);
      for (const d of slice) batch.delete(d.ref);
      await batch.commit();
    }

    // 3) Borrar member + actualizar contadores en una transacción
    //    (clamp a 0 para evitar contadores negativos por race conditions).
    await runTransaction(db, async (tx) => {
      const memberRef = doc(db, "members", id);
      const clubRef = doc(db, "clubs", clubId);
      const clubTxSnap = await tx.get(clubRef);
      tx.delete(memberRef);
      if (clubTxSnap.exists()) {
        const clubData = clubTxSnap.data() || {};
        const currentMember = Number(clubData.memberCount) || 0;
        const currentActive = Number(clubData.activeCount) || 0;
        const updates: Record<string, unknown> = {
          memberCount: Math.max(0, currentMember - 1),
        };
        if (status === "active") {
          updates.activeCount = Math.max(0, currentActive - 1);
        }
        tx.update(clubRef, updates);
      }
    });
  },

  async countByClub(clubId: string): Promise<number> {
    const members = await getAll<Member>("members", [
      where("clubId", "==", clubId),
      where("status", "==", "active"),
    ]);
    return members.length;
  },

  /**
   * Obtener miembros de múltiples clubes
   * Útil para cuentas de iglesia que tienen varios clubes
   */
  async getByClubs(clubIds: string[]): Promise<Member[]> {
    if (clubIds.length === 0) return [];

    // Firestore limita 'in' a 30 elementos, pero normalmente una iglesia tiene 3 clubes
    const members = await getAll<Member>("members", [
      where("clubId", "in", clubIds),
      where("status", "==", "active"),
    ]);
    return members;
  },

  /**
   * Obtener todos los miembros (solo para admins)
   */
  async getAll(): Promise<Member[]> {
    return getAll<Member>("members", [where("status", "==", "active")]);
  },

  /**
   * Obtener miembros de una asociación con datos de persona
   */
  async getByAssociationWithPerson(
    associationId: string,
  ): Promise<MemberWithPerson[]> {
    const members = await getAll<Member>("members", [
      where("associationId", "==", associationId),
      where("status", "==", "active"),
    ]);
    if (members.length === 0) return [];

    const personIds = [...new Set(members.map((m) => m.personId))];
    const persons = await personService.getByIds(personIds);
    const personMap = new Map(persons.map((p) => [p.id, p]));

    return members
      .filter((m) => personMap.has(m.personId))
      .map((m) => toMemberWithPerson(m, personMap.get(m.personId)!));
  },

  /**
   * Obtener miembros de una asociación con datos de persona (formato legacy)
   * @deprecated Usar getByAssociationWithPerson
   */
  async getByAssociationLegacy(associationId: string): Promise<MemberLegacy[]> {
    const members = await getAll<Member>("members", [
      where("associationId", "==", associationId),
      where("status", "==", "active"),
    ]);
    if (members.length === 0) return [];

    const personIds = [...new Set(members.map((m) => m.personId))];
    const persons = await personService.getByIds(personIds);
    const personMap = new Map(persons.map((p) => [p.id, p]));

    return members
      .filter((m) => personMap.has(m.personId))
      .map((m) => toMemberLegacy(m, personMap.get(m.personId)!));
  },

  /**
   * Obtener todos los miembros con datos de persona (solo admins)
   */
  async getAllWithPerson(): Promise<MemberWithPerson[]> {
    const members = await this.getAll();
    if (members.length === 0) return [];

    const personIds = [...new Set(members.map((m) => m.personId))];
    const persons = await personService.getByIds(personIds);
    const personMap = new Map(persons.map((p) => [p.id, p]));

    return members
      .filter((m) => personMap.has(m.personId))
      .map((m) => toMemberWithPerson(m, personMap.get(m.personId)!));
  },

  /**
   * Obtener todos los miembros con datos de persona (formato legacy, solo admins)
   * @deprecated Usar getAllWithPerson
   */
  async getAllLegacy(): Promise<MemberLegacy[]> {
    const members = await this.getAll();
    if (members.length === 0) return [];

    const personIds = [...new Set(members.map((m) => m.personId))];
    const persons = await personService.getByIds(personIds);
    const personMap = new Map(persons.map((p) => [p.id, p]));

    return members
      .filter((m) => personMap.has(m.personId))
      .map((m) => toMemberLegacy(m, personMap.get(m.personId)!));
  },

  /**
   * Obtener todas las membresías de una persona
   * Usado para mostrar "Mis Clubes" al usuario o ver historial de membresías
   */
  async getByPersonId(personId: string): Promise<Member[]> {
    return getAll<Member>("members", [
      where("personId", "==", personId),
      orderBy("createdAt", "desc"),
    ]);
  },

  /**
   * Obtener todas las membresías de un usuario (por linkedUserId en Person)
   * Primero busca la Person vinculada, luego sus membresías
   */
  async getByLinkedUser(userId: string): Promise<Member[]> {
    // Buscar la persona vinculada a este usuario
    const person = await personService.getByLinkedUser(userId);
    if (!person) return [];

    // Obtener todas las membresías de esa persona
    return this.getByPersonId(person.id);
  },

  /**
   * Cambiar el rol de un miembro dentro de su club
   * Guarda el rol anterior en el historial
   */
  async changeRole(
    memberId: string,
    newRole: Member["role"],
    period: string,
    changedBy: string,
  ): Promise<void> {
    const member = await getById<Member>("members", memberId);
    if (!member) throw new Error("Miembro no encontrado");

    const now = Timestamp.now();

    // Preparar entrada de historial para el rol anterior
    const previousRoleEntry = {
      role: member.role,
      period: period,
      startDate: member.roleStartDate || member.createdAt,
      endDate: now,
    };

    // Actualizar miembro con nuevo rol y agregar al historial
    const currentHistory = member.roleHistory || [];

    await update<Member>("members", memberId, {
      role: newRole,
      roleStartDate: now,
      roleHistory: [...currentHistory, previousRoleEntry],
      updatedBy: changedBy,
    });
  },

  /**
   * Crear membresía para una persona existente
   * Incluye datos desnormalizados de la persona para visualización
   */
  async createForPerson(
    personId: string,
    memberData: Omit<
      Member,
      | "id"
      | "createdAt"
      | "updatedAt"
      | "personId"
      | "personName"
      | "personPhoto"
    >,
    userId: string,
  ): Promise<string> {
    // Obtener datos de la persona para desnormalizar
    const person = await personService.getById(personId);
    if (!person) throw new Error("Persona no encontrada");

    // Verificar que no exista ya una membresía en el mismo club/período
    if (memberData.periodId) {
      const existing = await this.findExisting(
        personId,
        memberData.clubId,
        memberData.periodId,
      );
      if (existing) {
        throw new Error(
          "Ya existe una membresía para esta persona en este club y período",
        );
      }
    }

    return create<Member>(
      "members",
      {
        ...memberData,
        personId,
        personName: person.fullName,
        personPhoto: person.photoUrl,
      },
      userId,
    );
  },

  /**
   * Actualizar datos desnormalizados cuando cambia la persona
   * Llamar cuando se actualiza Person.fullName o Person.photoUrl
   */
  async syncPersonData(
    personId: string,
    fullName: string,
    photoUrl?: string,
  ): Promise<void> {
    const members = await this.getByPersonId(personId);

    await Promise.all(
      members.map((member) =>
        update<Member>("members", member.id, {
          personName: fullName,
          personPhoto: photoUrl,
        }),
      ),
    );
  },

  /**
   * Vincular todas las personas con un email dado a un usuario.
   *
   * BLINDADO POST-FASE-7:
   *   - Busca tanto en `email` como en `_legacyEmail` (cubre personas migradas
   *     al guardian model que ya no tienen el email original directo).
   *   - Solo vincula como `linkedUserId` cuando hay coincidencia razonable de
   *     nombre (similitud Jaccard ≥ 0.5). Si no, NO toca el documento desde
   *     el cliente — se delega al trigger del servidor `onUserCreated` que
   *     puede asignar `guardianUserId` con privilegios admin.
   *   - Salta personas con `_duplicateOf` o ya vinculadas/guardianadas a otro uid.
   *
   * @param email Email del usuario que se acaba de registrar
   * @param userId UID del usuario
   * @param userFullName Nombre que el usuario reportó al registrarse (opcional
   *   pero recomendado para validar similitud)
   * @returns Cantidad de personas vinculadas como linkedUserId
   */
  async linkAllByEmailToUser(
    email: string,
    userId: string,
    userFullName?: string,
  ): Promise<number> {
    const [byEmail, byLegacy] = await Promise.all([
      personService.getAllByEmail(email),
      personService.getAllByLegacyEmail(email),
    ]);
    // De-duplicar por id
    const byId = new Map<string, Person>();
    for (const p of byEmail) byId.set(p.id, p);
    for (const p of byLegacy) byId.set(p.id, p);

    let linkedCount = 0;

    for (const person of byId.values()) {
      // Skip si ya está vinculada a otro usuario
      if (person.linkedUserId && person.linkedUserId !== userId) continue;
      // Skip si ya tiene acudiente asignado
      if (person.guardianUserId && person.guardianUserId !== userId) continue;
      // Skip duplicados marcados
      if ((person as Person & { _duplicateOf?: string })._duplicateOf) continue;
      // Skip si ya está vinculada a este mismo user (idempotencia)
      if (person.linkedUserId === userId) continue;

      // Si no hay nombre del user no podemos validar similitud → salto y dejo
      // que el trigger del servidor decida.
      if (!userFullName) continue;

      const sim = nameSimilarity(person.fullName ?? "", userFullName);
      if (sim < 0.5) {
        // Nombre no coincide → probablemente es un acudiente. NO vincular
        // desde el cliente; el trigger del servidor lo marcará como guardian.
        continue;
      }

      try {
        await personService.linkToUser(
          person.id,
          userId,
          undefined,
          userFullName,
        );
        linkedCount++;
      } catch (err) {
        console.warn(
          `[linkAllByEmailToUser] No se vinculó persona ${person.id}:`,
          err,
        );
      }
    }

    return linkedCount;
  },
};

// ==================== REALTIME SUBSCRIPTIONS ====================

/** Suscripción en tiempo real a miembros de un club CON datos de persona */
export function subscribeToMembersByClub(
  clubId: string,
  callback: (members: MemberWithPerson[]) => void,
  onError?: (error: Error) => void,
): () => void {
  const q = query(collection(db, "members"), where("clubId", "==", clubId));
  return onSnapshot(
    q,
    async (snapshot) => {
      const members = snapshot.docs
        .map((d) => convertTimestamps({ id: d.id, ...d.data() }) as Member)
        .sort((a, b) => {
          const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(0);
          const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(0);
          return dateB.getTime() - dateA.getTime();
        });
      if (members.length === 0) {
        callback([]);
        return;
      }
      const personIds = [...new Set(members.map((m) => m.personId))];
      const persons = await personService.getByIds(personIds);
      const personMap = new Map(persons.map((p) => [p.id, p]));
      callback(
        members
          .filter((m) => personMap.has(m.personId))
          .map((m) => toMemberWithPerson(m, personMap.get(m.personId)!)),
      );
    },
    (error) => {
      console.error("[Firestore] subscribeToMembersByClub error:", error);
      if (onError) onError(error);
    },
  );
}

/** Suscripción en tiempo real a miembros de una asociación CON datos de persona */
export function subscribeToMembersByAssociation(
  associationId: string,
  callback: (members: MemberWithPerson[]) => void,
  onError?: (error: Error) => void,
): () => void {
  const q = query(
    collection(db, "members"),
    where("associationId", "==", associationId),
    where("status", "==", "active"),
  );
  return onSnapshot(
    q,
    async (snapshot) => {
      const members = snapshot.docs
        .map((d) => convertTimestamps({ id: d.id, ...d.data() }) as Member)
        .sort((a, b) => {
          const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(0);
          const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(0);
          return dateB.getTime() - dateA.getTime();
        });
      if (members.length === 0) {
        callback([]);
        return;
      }
      const personIds = [...new Set(members.map((m) => m.personId))];
      const persons = await personService.getByIds(personIds);
      const personMap = new Map(persons.map((p) => [p.id, p]));
      callback(
        members
          .filter((m) => personMap.has(m.personId))
          .map((m) => toMemberWithPerson(m, personMap.get(m.personId)!)),
      );
    },
    (error) => {
      console.error(
        "[Firestore] subscribeToMembersByAssociation error:",
        error,
      );
      if (onError) onError(error);
    },
  );
}

/** Suscripción en tiempo real a TODOS los miembros activos CON datos de persona */
export function subscribeToAllMembers(
  callback: (members: MemberWithPerson[]) => void,
  onError?: (error: Error) => void,
): () => void {
  const q = query(collection(db, "members"), where("status", "==", "active"));
  return onSnapshot(
    q,
    async (snapshot) => {
      const members = snapshot.docs
        .map((d) => convertTimestamps({ id: d.id, ...d.data() }) as Member)
        .sort((a, b) => {
          const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(0);
          const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(0);
          return dateB.getTime() - dateA.getTime();
        });
      if (members.length === 0) {
        callback([]);
        return;
      }
      const personIds = [...new Set(members.map((m) => m.personId))];
      const persons = await personService.getByIds(personIds);
      const personMap = new Map(persons.map((p) => [p.id, p]));
      callback(
        members
          .filter((m) => personMap.has(m.personId))
          .map((m) => toMemberWithPerson(m, personMap.get(m.personId)!)),
      );
    },
    (error) => {
      console.error("[Firestore] subscribeToAllMembers error:", error);
      if (onError) onError(error);
    },
  );
}

/** Suscripción en tiempo real a las membresías de una persona */
export function subscribeToMembersByPerson(
  personId: string,
  callback: (members: Member[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return subscribeToCollection<Member>(
    "members",
    callback,
    [where("personId", "==", personId), orderBy("createdAt", "desc")],
    onError,
  );
}

/** Suscripción en tiempo real a miembros de una unidad */
export function subscribeToMembersByUnit(
  unitId: string,
  callback: (members: Member[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return subscribeToCollection<Member>(
    "members",
    callback,
    [where("unitId", "==", unitId), where("status", "==", "active")],
    onError,
  );
}

/** Suscripción en tiempo real a un miembro con datos de persona (legacy format) */
export function subscribeToMember(
  id: string,
  callback: (member: MemberWithPerson | null) => void,
  onError?: (error: Error) => void,
): () => void {
  return onSnapshot(
    doc(db, "members", id),
    async (docSnap) => {
      if (!docSnap.exists()) {
        callback(null);
        return;
      }
      const member = convertTimestamps({
        id: docSnap.id,
        ...docSnap.data(),
      }) as Member;
      try {
        const person = await personService.getById(member.personId);
        if (!person) {
          callback(null);
          return;
        }
        callback({
          ...member,
          person,
          personName:
            person.fullName || `${person.firstName} ${person.lastName}`,
          personPhoto: person.photoUrl,
        } as MemberWithPerson);
      } catch {
        callback(null);
      }
    },
    (error) => {
      console.error("[Firestore] subscribeToMember error:", error);
      if (onError) onError(error);
    },
  );
}

/** Suscripción en tiempo real a membresías por linkedUserId (busca persona, luego membresías) */
export function subscribeToMembersByLinkedUser(
  userId: string,
  callback: (members: Member[]) => void,
  onError?: (error: Error) => void,
): () => void {
  // Primero buscamos la persona vinculada a este usuario
  const personQuery = query(
    collection(db, "persons"),
    where("linkedUserId", "==", userId),
    limit(1),
  );

  return onSnapshot(
    personQuery,
    (personSnapshot) => {
      if (personSnapshot.empty) {
        callback([]);
        return;
      }
      const person = personSnapshot.docs[0];
      // Suscribirse a las membresías de la persona encontrada
      const membersQuery = query(
        collection(db, "members"),
        where("personId", "==", person.id),
        orderBy("createdAt", "desc"),
      );
      // Para simplificar, hacemos un getDocs aquí ya que el trigger externo
      // (cambio en la persona) re-ejecutará todo el listener
      getDocs(membersQuery)
        .then((membersSnap) => {
          callback(
            membersSnap.docs.map(
              (d) => convertTimestamps({ id: d.id, ...d.data() }) as Member,
            ),
          );
        })
        .catch((err) => {
          console.error(
            "[Firestore] subscribeToMembersByLinkedUser members error:",
            err,
          );
          if (onError) onError(err);
        });
    },
    (error) => {
      console.error("[Firestore] subscribeToMembersByLinkedUser error:", error);
      if (onError) onError(error);
    },
  );
}

// Re-exportar personService para compatibilidad
export { personService } from "./persons";
